import { useState, useCallback } from 'react';

/**
 * Strips file reference citations from markdown text
 * Removes patterns like [→](fileId@timestamp) that appear in chat responses
 *
 * @param text - The markdown text containing references
 * @returns The text with references removed
 *
 * @example
 * stripReferences("Check this [→](abc123@45) for more info")
 * // Returns: "Check this for more info"
 */
export const stripReferences = (text: string): string => {
  // Remove reference links like [→](fileId@timestamp)
  // This regex matches: [any text](something@something)
  return text.replace(/\s*\[→\]\([^)]+@[^)]+\)/g, '');
};

/**
 * Hook for managing copy-to-clipboard functionality with success feedback
 * Automatically resets success state after 2 seconds
 *
 * @returns Object containing copy function and success state
 *
 * @example
 * const { copyToClipboard, copySuccess } = useCopyToClipboard();
 * // Later in your component:
 * <button onClick={() => copyToClipboard("text to copy")}>
 *   {copySuccess ? <CheckIcon /> : <CopyIcon />}
 * </button>
 */
export const useCopyToClipboard = () => {
  const [copySuccess, setCopySuccess] = useState(false);

  const copyToClipboard = useCallback((text: string) => {
    if (text) {
      navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, []);

  return { copyToClipboard, copySuccess };
};

/**
 * Hook for tracking which message in a list was copied
 * Useful for showing copy success indicator on specific messages
 * Automatically strips file references from the text before copying
 *
 * @returns Object containing copy handler and copied message index
 *
 * @example
 * const { handleCopy, copiedIndex } = useMessageCopy();
 * // In your message list:
 * messages.map((msg, i) => (
 *   <button onClick={() => handleCopy(msg.content, i)}>
 *     {copiedIndex === i ? <CheckIcon /> : <CopyIcon />}
 *   </button>
 * ))
 */
export const useMessageCopy = () => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = useCallback((text: string, index: number) => {
    // Strip references before copying
    const cleanText = stripReferences(text);
    navigator.clipboard.writeText(cleanText);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  }, []);

  return { handleCopy, copiedIndex };
};
